import React, { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import { Badge } from 'components/ui/badge'
import { ArrowUpDown, ChevronUp, ChevronDown, ChevronRight, MapPin } from 'lucide-react'
import { TablePagination } from 'components/ui/pagination'
import MapLocationPopupCN from 'components/popups/MapLocationPopupCN'
import ProjectActionSheet from './ProjectActionSheet'
import useIsMobile from 'hooks/useIsMobile'
import usePartnerProfileDialog from 'features/Partners/hooks/usePartnerProfileDialog'

const sortFields = [
	{ id: 'projectName', label: 'Name' },
	{ id: 'assetName', label: 'Location' },
	{ id: 'city', label: 'City' },
	{ id: 'stateProvince', label: 'State' },
	{ id: 'projectType', label: 'Project Type' },
	{ id: 'updatedAt', label: 'Date Updated' },
]

const ProjectsListCN = ({ projects, recordCount, userId }) => {
	const isMobile = useIsMobile()
	const [page, setPage] = useState(0)
	const [rowsPerPage, setRowsPerPage] = useState(10)
	const [orderBy, setOrderBy] = useState(null)
	const [order, setOrder] = useState('asc')

	// Sheet state
	const [sheetOpen, setSheetOpen] = useState(false)
	const [selectedProject, setSelectedProject] = useState(null)

	// The owner avatar opens the shared partner profile popup (MYD-535): the
	// same one every partner list opens, rather than a hand-rolled overlay.
	const { openPartner, partnerDialog } = usePartnerProfileDialog()

	// Map popup state
	const [openMap, setOpenMap] = useState(false)
	const [mapLocation, setMapLocation] = useState(null)

	const handleThreeDotClick = (event, project) => {
		event.stopPropagation()
		setSelectedProject(project)
		setSheetOpen(true)
	}

	// A project row carries its owner as an id plus flattened fields, so the
	// popup's fallback is assembled from those.
	const handleAvatarClick = (event, record) => {
		event.stopPropagation()
		const ownerId = typeof record?.owner === 'object' ? record?.owner?._id : record?.owner
		openPartner(ownerId, {
			name: record?.ownerName,
			image: record?.ownerImage,
			email: record?.ownerEmail,
			company: record?.ownerCompany,
		})
	}

	const handleOpenMap = (e, project) => {
		e.stopPropagation()
		e.preventDefault()
		setMapLocation(project)
		setOpenMap(true)
	}

	const handleSort = fieldId => {
		if (orderBy === fieldId) {
			setOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))
		} else {
			setOrderBy(fieldId)
			setOrder('asc')
		}
		setPage(0)
	}

	const sortedProjects = useMemo(() => {
		if (!orderBy) return projects

		return [...projects].sort((a, b) => {
			if (orderBy === 'updatedAt') {
				const aVal = new Date(a.updatedAt).getTime() || 0
				const bVal = new Date(b.updatedAt).getTime() || 0
				if (aVal < bVal) return order === 'asc' ? -1 : 1
				if (aVal > bVal) return order === 'asc' ? 1 : -1
				return 0
			}
			const aVal = (a[orderBy] || '').toString().toLowerCase()
			const bVal = (b[orderBy] || '').toString().toLowerCase()
			if (aVal < bVal) return order === 'asc' ? -1 : 1
			if (aVal > bVal) return order === 'asc' ? 1 : -1
			return 0
		})
	}, [projects, orderBy, order])

	const paginatedProjects = useMemo(() => {
		// On a phone the list is unpaginated and scrolled, so every row renders.
		// Hiding the pager without this would strand every record past the tenth.
		if (isMobile) return sortedProjects
		const start = page * rowsPerPage
		return sortedProjects.slice(start, start + rowsPerPage)
	}, [sortedProjects, page, rowsPerPage, isMobile])

	const SortIcon = ({ fieldId }) => {
		if (orderBy !== fieldId) return <ArrowUpDown className="h-3 w-3 text-gray-300" />
		return order === 'asc' ? (
			<ChevronUp className="h-3 w-3 text-[#0080fe]" />
		) : (
			<ChevronDown className="h-3 w-3 text-[#0080fe]" />
		)
	}

	const formatDate = dateStr => {
		if (!dateStr) return ''
		return new Date(dateStr).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
	}

	return (
		<>
			<div className="rounded-lg border border-gray-200 overflow-hidden shadow-sm bg-white p-4">
				{/* Sort pills. Hidden on mobile: five pills wrap onto two lines at
				    390px, costing most of the first screenful before a single record
				    appears. Mobile only, deliberately — this list renders flex rows
				    rather than a real table, so there is no column header behind
				    these and hiding them everywhere would strip sorting from the
				    desktop List view entirely. The sort state itself is untouched. */}
				{!isMobile && (
				<div className="mb-3 flex items-center gap-1.5 flex-wrap">
					<span className="text-xs text-gray-400 mr-1">Sort by:</span>
					{sortFields.map(field => (
						<button
							key={field.id}
							type="button"
							onClick={() => handleSort(field.id)}
							className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
								orderBy === field.id ? 'bg-[#0080fe]/10 text-[#0080fe]' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
							}`}
						>
							{field.label}
							<SortIcon fieldId={field.id} />
						</button>
					))}
				</div>
				)}

				{/* List items */}
				<div className="divide-y divide-gray-100">
					{paginatedProjects.map(project => (
						<div key={project._id} className="flex items-center gap-3 py-2 group">
							{/* Avatar */}
							<Avatar className="h-8 w-8 flex-shrink-0">
								<AvatarImage src={project.image} />
								<AvatarFallback className="text-[10px] bg-gray-100 text-gray-400">
									{project.projectName?.[0] || '?'}
								</AvatarFallback>
							</Avatar>

							{/* Project name */}
							<Link
								to={`/projects/${project._id}/tasks`}
								className="text-sm font-medium text-primary hover:underline truncate min-w-0"
							>
								{project.projectName}
							</Link>

							{/* Location name */}
							<span className="text-sm text-gray-500 truncate min-w-0">{project.assetName}</span>

							{/* Address pill with map popup */}
							<button
								type="button"
								onClick={e => handleOpenMap(e, project)}
								className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-xs text-gray-600 hover:bg-[#0080fe]/10 hover:text-[#0080fe] hover:border-[#0080fe]/20 transition-colors cursor-pointer flex-shrink-0"
							>
								<MapPin className="h-3 w-3" />
								{project.address}
							</button>

							{/* City */}
							<span className="text-sm text-gray-500 truncate min-w-0">{project.city}</span>

							{/* State */}
							<span className="text-sm text-gray-500 flex-shrink-0">{project.stateProvince}</span>

							{/* Project type badge */}
							{project.projectType && (
								<Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 flex-shrink-0">
									{project.projectType}
								</Badge>
							)}

							{/* Updated date */}
							{project.updatedAt && (
								<span className="text-[10px] text-gray-400 flex-shrink-0 whitespace-nowrap">
									{formatDate(project.updatedAt)}
								</span>
							)}

							{/* Edit — right-justified */}
							<div className="ml-auto flex-shrink-0 opacity-50 group-hover:opacity-100 transition-opacity">
								{project.owner === userId ? (
									<button
										type="button"
										className="inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100 transition-colors"
										onClick={e => handleThreeDotClick(e, project)}
									>
										<ChevronRight className="h-4 w-4" />
									</button>
								) : (
									<Avatar className="h-7 w-7 cursor-pointer" onClick={e => handleAvatarClick(e, project)}>
										<AvatarImage src={project.ownerImage} />
										<AvatarFallback />
									</Avatar>
								)}
							</div>
						</div>
					))}
				</div>

				{!isMobile && recordCount > 10 && (
					<TablePagination
						page={page}
						rowsPerPage={rowsPerPage}
						count={recordCount}
						onPageChange={setPage}
						onRowsPerPageChange={setRowsPerPage}
					/>
				)}
			</div>

			{/* Map popup */}
			{openMap && mapLocation && (
				<MapLocationPopupCN open={openMap} onClose={() => setOpenMap(false)} location={mapLocation} />
			)}

			{partnerDialog}

			<ProjectActionSheet project={selectedProject} open={sheetOpen} onClose={() => setSheetOpen(false)} />
		</>
	)
}

export default ProjectsListCN
